import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Loader2, Trash2, Eye, FileDown, User, FileSpreadsheet } from 'lucide-react';
import { recordsAPI, patientsAPI } from '../services/api';
import { useNavigate, useParams } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { generatePDF } from '../utils/pdfGenerator';
import { exportMicronutrientsToExcel } from '../utils/excelExporter';
import { calculateNutritionalStatus } from '../utils/nutritionalStatus';
import StatusDot from '../components/StatusDot';
import ConfirmDialog, { AlertDialog } from '../components/ConfirmDialog';
import { useConfirmDialog, useAlertDialog } from '../hooks/useDialog';
import { formatNumber, formatPercentage } from '../utils/numberFormat';
import { ComposedChart, BarChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import BackToTop from '../components/BackToTop';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

export default function History() {
  const navigate = useNavigate();
  const { patientId } = useParams();
  const { dialog: confirmDialog, confirm, close: closeConfirm } = useConfirmDialog();
  const { dialog: alertDialog, alert: showAlert, close: closeAlert } = useAlertDialog();
  
  const [patient, setPatient] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [user, setUser] = useState(null);

  // Get user info from token
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUser(payload);
      } catch (e) {
        console.error('Invalid token');
      }
    }
  }, []);

  // Helper to get max records based on role (unlimited for all roles)
  const getMaxRecords = () => {
    return Infinity;
  };

  useEffect(() => {
    loadPatientAndRecords();
  }, [patientId]);

  const loadPatientAndRecords = async () => {
    setLoading(true);
    try {
      // Load patient info
      if (patientId) {
        const patientData = await patientsAPI.getById(patientId);
        setPatient(patientData);
        
        // Load patient-specific records
        const patientRecords = await patientsAPI.getRecords(patientId);
        setRecords(patientRecords);
      } else {
        // Fallback to all records (legacy)
        const data = await recordsAPI.getAll();
        setRecords(data);
      }
    } catch (error) {
      console.error('Error loading records:', error);
      showAlert({
        type: 'danger',
        title: 'Error',
        message: 'Failed to load history'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (recordId, patientName) => {
    confirm({
      type: 'danger',
      title: 'Delete Calculation',
      message: `Delete calculation for "${patientName}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          await recordsAPI.delete(recordId);
          showAlert({
            type: 'success',
            title: 'Deleted',
            message: 'Record deleted successfully'
          });
          loadPatientAndRecords();
        } catch (error) {
          console.error('Error deleting record:', error);
          showAlert({
            type: 'danger',
            title: 'Error',
            message: 'Failed to delete record'
          });
        }
      }
    });
  };

  const handleViewDetails = (record) => {
    setSelectedRecord(record);
    setShowDetailDialog(true);
  };

  const handleExportPDF = async (record) => {
    setExporting(record.id);
    try {
      // Export single page daily report
      await generatePDF({ ...record, patient: patient || record.patient });
    } catch (error) {
      console.error('PDF export error:', error);
      showAlert({
        type: 'danger',
        title: 'Export Failed',
        message: 'Failed to export PDF: ' + error.message
      });
    } finally {
      setExporting(null);
    }
  };

  const handleExportHistoryPDF = async () => {
    setExporting('history');
    try {
      // Export history with graphs and table
      const { generateHistoryPDF } = await import('../utils/pdfGenerator');
      await generateHistoryPDF(patient, records, 'patient-history-graphs');
      showAlert({
        type: 'success',
        title: 'Export Success',
        message: 'History PDF has been downloaded successfully'
      });
    } catch (error) {
      console.error('History PDF export error:', error);
      showAlert({
        type: 'danger',
        title: 'Export Failed',
        message: 'Failed to export history PDF: ' + error.message
      });
    } finally {
      setExporting(null);
    }
  };

  const handleExportExcel = (record) => {
    try {
      // Reconstruct micronutrients from saved record items
      const reconstructMicronutrients = (oralItems, parenteralItems) => {
        // Oral micronutrients
        const oralMicro = {
          energy_kcal: record.oral_enteral_totals.energy_kcal,
          protein_g: record.oral_enteral_totals.protein_g,
          carbohydrate_g: record.oral_enteral_totals.carbohydrate_g,
          fat_g: record.oral_enteral_totals.fat_g,
          fiber_g: 0,
          sodium_mg: 0,
          potassium_mg: 0,
          chloride_mg: 0,
          omega3_mg: 0,
          omega6_mg: 0,
          vitamin_a_mcg: 0,
          vitamin_d3_mcg: 0,
          vitamin_e_mcg: 0,
          vitamin_k_mcg: 0,
          vitamin_b1_mg: 0,
          vitamin_b2_mg: 0,
          vitamin_b3_mg: 0,
          vitamin_b5_mg: 0,
          vitamin_b6_mg: 0,
          vitamin_b12_mcg: 0,
          folic_acid_mcg: 0,
          biotin_mcg: 0,
          choline_mg: 0,
          vitamin_c_mg: 0,
          calcium_mg: 0,
          phosphorus_mg: 0,
          magnesium_mg: 0,
          iron_mg: 0,
          iodine_mcg: 0,
          zinc_mg: 0,
          selenium_mcg: 0,
          manganese_mg: 0,
          fluoride_mg: 0,
          chromium_mcg: 0,
          copper_mg: 0,
        };

        // Accumulate from oral items
        oralItems.forEach(item => {
          if (item.fiber_g !== undefined) oralMicro.fiber_g += item.fiber_g;
          if (item.sodium_mg !== undefined) oralMicro.sodium_mg += item.sodium_mg;
          if (item.potassium_mg !== undefined) oralMicro.potassium_mg += item.potassium_mg;
          if (item.chloride_mg !== undefined) oralMicro.chloride_mg += item.chloride_mg;
          if (item.omega3_mg !== undefined) oralMicro.omega3_mg += item.omega3_mg;
          if (item.omega6_mg !== undefined) oralMicro.omega6_mg += item.omega6_mg;
          if (item.vitamin_a_mcg !== undefined) oralMicro.vitamin_a_mcg += item.vitamin_a_mcg;
          if (item.vitamin_d3_mcg !== undefined) oralMicro.vitamin_d3_mcg += item.vitamin_d3_mcg;
          if (item.vitamin_e_mcg !== undefined) oralMicro.vitamin_e_mcg += item.vitamin_e_mcg;
          if (item.vitamin_k_mcg !== undefined) oralMicro.vitamin_k_mcg += item.vitamin_k_mcg;
          if (item.vitamin_b1_mg !== undefined) oralMicro.vitamin_b1_mg += item.vitamin_b1_mg;
          if (item.vitamin_b2_mg !== undefined) oralMicro.vitamin_b2_mg += item.vitamin_b2_mg;
          if (item.vitamin_b3_mg !== undefined) oralMicro.vitamin_b3_mg += item.vitamin_b3_mg;
          if (item.vitamin_b5_mg !== undefined) oralMicro.vitamin_b5_mg += item.vitamin_b5_mg;
          if (item.vitamin_b6_mg !== undefined) oralMicro.vitamin_b6_mg += item.vitamin_b6_mg;
          if (item.vitamin_b12_mcg !== undefined) oralMicro.vitamin_b12_mcg += item.vitamin_b12_mcg;
          if (item.folic_acid_mcg !== undefined) oralMicro.folic_acid_mcg += item.folic_acid_mcg;
          if (item.biotin_mcg !== undefined) oralMicro.biotin_mcg += item.biotin_mcg;
          if (item.choline_mg !== undefined) oralMicro.choline_mg += item.choline_mg;
          if (item.vitamin_c_mg !== undefined) oralMicro.vitamin_c_mg += item.vitamin_c_mg;
          if (item.calcium_mg !== undefined) oralMicro.calcium_mg += item.calcium_mg;
          if (item.phosphorus_mg !== undefined) oralMicro.phosphorus_mg += item.phosphorus_mg;
          if (item.magnesium_mg !== undefined) oralMicro.magnesium_mg += item.magnesium_mg;
          if (item.iron_mg !== undefined) oralMicro.iron_mg += item.iron_mg;
          if (item.iodine_mcg !== undefined) oralMicro.iodine_mcg += item.iodine_mcg;
          if (item.zinc_mg !== undefined) oralMicro.zinc_mg += item.zinc_mg;
          if (item.selenium_mcg !== undefined) oralMicro.selenium_mcg += item.selenium_mcg;
          if (item.manganese_mg !== undefined) oralMicro.manganese_mg += item.manganese_mg;
          if (item.fluoride_mg !== undefined) oralMicro.fluoride_mg += item.fluoride_mg;
          if (item.chromium_mcg !== undefined) oralMicro.chromium_mcg += item.chromium_mcg;
          if (item.copper_mg !== undefined) oralMicro.copper_mg += item.copper_mg;
        });

        // Parenteral micronutrients
        const parenteralMicro = {
          energy_kcal: record.parenteral_totals.energy_kcal,
          protein_g: record.parenteral_totals.protein_g,
          carbohydrate_g: record.parenteral_totals.carbohydrate_g,
          fat_g: record.parenteral_totals.fat_g,
          sodium_mmol: 0,
          potassium_mmol: 0,
          calcium_mmol: 0,
          chloride_mmol: 0,
          magnesium_mmol: 0,
          phosphate_mmol: 0,
          lactate_mmol: 0,
          acetate_meq: 0,
          nitrogen_g: 0,
        };

        // Accumulate from parenteral items
        parenteralItems.forEach(item => {
          if (item.sodium_mmol !== undefined) parenteralMicro.sodium_mmol += item.sodium_mmol;
          if (item.potassium_mmol !== undefined) parenteralMicro.potassium_mmol += item.potassium_mmol;
          if (item.calcium_mmol !== undefined) parenteralMicro.calcium_mmol += item.calcium_mmol;
          if (item.chloride_mmol !== undefined) parenteralMicro.chloride_mmol += item.chloride_mmol;
          if (item.magnesium_mmol !== undefined) parenteralMicro.magnesium_mmol += item.magnesium_mmol;
          if (item.phosphate_mmol !== undefined) parenteralMicro.phosphate_mmol += item.phosphate_mmol;
          if (item.lactate_mmol !== undefined) parenteralMicro.lactate_mmol += item.lactate_mmol;
          if (item.acetate_meq !== undefined) parenteralMicro.acetate_meq += item.acetate_meq;
          if (item.nitrogen_g !== undefined) parenteralMicro.nitrogen_g += item.nitrogen_g;
        });

        return { oral: oralMicro, parenteral: parenteralMicro };
      };

      const micronutrients = reconstructMicronutrients(
        record.oral_enteral_items,
        record.parenteral_items
      );

      // Calculate Sodium and Potassium in mEq
      const sodiumMeq = {
        oral: micronutrients.oral.sodium_mg / 23,
        parenteral: micronutrients.parenteral.sodium_mmol,
        total: (micronutrients.oral.sodium_mg / 23) + micronutrients.parenteral.sodium_mmol
      };

      const potassiumMeq = {
        oral: micronutrients.oral.potassium_mg / 39.1,
        parenteral: micronutrients.parenteral.potassium_mmol,
        total: (micronutrients.oral.potassium_mg / 39.1) + micronutrients.parenteral.potassium_mmol
      };

      const filename = `NUTRIMUS_Macro-Micronutrients_${record.date}.xlsx`;
      exportMicronutrientsToExcel(
        {
          patient: {
            name: record.patient_name,
            gender: patient?.gender,
            age: patient?.age
          },
          date: record.date,
          targetEnergy: record.target_energy_kcal,
          micronutrients,
          sodiumMeq,
          potassiumMeq
        },
        filename
      );

      showAlert({
        type: 'success',
        title: 'Export Successful',
        message: 'Micronutrient data has been exported to Excel'
      });
    } catch (error) {
      console.error('Excel export error:', error);
      showAlert({
        type: 'danger',
        title: 'Export Failed',
        message: 'Failed to export Excel: ' + error.message
      });
    }
  };

  const getStatusColor = (percentage) => {
    if (percentage < 80) return 'text-yellow-600 bg-yellow-50';
    if (percentage > 120) return 'text-red-600 bg-red-50';
    return 'text-green-600 bg-green-50';
  };

  // Prepare chart data (last 7 days)
  const prepareChartData = () => {
    if (!records || records.length === 0) return [];
    
    // Sort records by date descending and take last 7
    const sortedRecords = [...records]
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 7)
      .reverse(); // Reverse to show chronologically
    
    return sortedRecords.map(record => {
      const recordDate = new Date(record.date);
      // Format date as DD/MM/YY
      const day = String(recordDate.getDate()).padStart(2, '0');
      const month = String(recordDate.getMonth() + 1).padStart(2, '0');
      const year = String(recordDate.getFullYear()).slice(-2);
      const shortDate = `${day}/${month}/${year}`;
      
      return {
        date: shortDate,
        fullDate: recordDate.toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' }),
        energy: parseFloat(record.grand_totals.energy_kcal.toFixed(1)),
        target: parseFloat(record.target_energy_kcal),
        protein: parseFloat(record.grand_totals.percentage_protein || 0),
        carbs: parseFloat(record.grand_totals.percentage_carbs || 0),
        fat: parseFloat(record.grand_totals.percentage_fat || 0)
      };
    });
  };

  const chartData = prepareChartData();

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label, valueType = 'number' }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
          <p className="text-sm font-semibold mb-1">{payload[0]?.payload?.fullDate || label}</p>
          {payload.map((entry) => (
            <p key={entry.dataKey || entry.name} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {valueType === 'percentage' 
                ? formatPercentage(entry.value, 1) 
                : formatNumber(entry.value, 1)}
              {valueType === 'kcal' && ' Kcal'}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Custom label for bars
  const renderCustomLabel = (props) => {
    const { x, y, width, height, value, valueType } = props;
    const formattedValue = valueType === 'percentage' 
      ? formatPercentage(value, 1)
      : formatNumber(value, 1);
    
    return (
      <text 
        x={x + width / 2} 
        y={Math.max(y - 8, 10)} 
        fill="#000" 
        textAnchor="middle" 
        fontSize="12"
        fontWeight="600"
      >
        {formattedValue}
      </text>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 flex flex-col justify-between">
      <div>
        <Navbar 
          user={user} 
          onLogout={() => { localStorage.removeItem('token'); navigate('/'); }} 
          breadcrumbs={[
            { label: 'Daftar Pasien', href: '/patients' },
            { label: patient?.name || 'Pasien', href: patientId ? `/patients/${patientId}/calculate` : '/patients' },
            { label: 'Riwayat Recall' }
          ]}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">
          {/* Clean Patient Header - Mobile Optimized */}
          {patient && (
            <div className="bg-white rounded-xl shadow-xs p-4 sm:p-5 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center font-bold text-sm flex-shrink-0">
                  <User className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-bold text-base md:text-lg text-slate-900 truncate">
                    {patient.name} — Riwayat Rekaman
                  </h2>
                  <div className="flex flex-wrap gap-2 md:gap-3 text-xs text-slate-500 mt-0.5">
                    <span>Usia: {patient.age} tahun</span>
                    <span>•</span>
                    <span>Gender: {patient.gender === 'male' ? 'Laki-laki' : 'Perempuan'}</span>
                    <span>•</span>
                    <span>Total: {records.length} rekaman</span>
                  </div>
                </div>
              </div>
            </div>
          )}

        {/* Back to Patient Button - Separated */}
        <div className="px-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/patients/${patientId}/calculate`)}
            className="w-full md:w-auto"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Patient
          </Button>
        </div>

        {/* History Graphs - Last 7 Days */}
        {chartData.length > 0 && (
          <Card className="bg-white shadow-lg" id="patient-history-graphs">
            <CardHeader className="p-4">
              <div className="flex flex-col gap-3">
                <div>
                  <CardTitle className="text-lg md:text-xl">Patient History Overview</CardTitle>
                  <CardDescription className="text-sm">Last 7 days of nutrition calculations</CardDescription>
                </div>
                <Button 
                  onClick={handleExportHistoryPDF}
                  disabled={exporting === 'history'}
                  className="bg-primary hover:bg-primary/90 w-full md:w-auto"
                  size="sm"
                >
                  <FileDown className="h-4 w-4 mr-2" />
                  {exporting === 'history' ? 'Exporting...' : 'Export History PDF'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-3 md:p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {/* Energy Graph - Combo Chart */}
                <div className="bg-white p-4 md:p-6 rounded-xl border-2 border-gray-200 shadow-md">
                  <h3 className="font-bold text-gray-800 mb-6 text-center text-base md:text-lg uppercase">
                    Energy
                  </h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <ComposedChart data={chartData} margin={{ top: 30, right: 5, bottom: 40, left: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 11, fill: '#64748b' }}
                        axisLine={false}
                        tickLine={false}
                        dy={10}
                        angle={-45}
                        textAnchor="end"
                      />
                      <YAxis 
                        tick={{ fontSize: 11, fill: '#64748b' }}
                        tickFormatter={(value) => value.toLocaleString('id-ID')}
                        axisLine={false}
                        tickLine={false}
                        width={45}
                      />
                      <Tooltip content={<CustomTooltip valueType="kcal" />} cursor={{ fill: '#f8fafc' }} />
                      <Legend 
                        wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
                        iconType="circle"
                      />
                      <Bar 
                        dataKey="energy" 
                        fill="#f97316" 
                        name="Energy"
                        radius={[2, 2, 0, 0]}
                        isAnimationActive={false}
                        barSize={50}
                        label={{ position: 'top', fill: '#1e293b', fontSize: 12, fontWeight: 'bold', formatter: (val) => formatNumber(val, 1) }}
                      />
                      <Line 
                        type="linear" 
                        dataKey="target" 
                        stroke="#3b82f6" 
                        strokeWidth={2}
                        name="Target"
                        dot={{ r: 4, strokeWidth: 2, fill: '#fff', stroke: '#3b82f6' }}
                        activeDot={{ r: 6, fill: '#3b82f6', stroke: '#fff', strokeWidth: 2 }}
                        isAnimationActive={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>

                {/* Protein Graph */}
                <div className="bg-white p-4 md:p-6 rounded-xl border-2 border-gray-200 shadow-md">
                  <h3 className="font-bold text-gray-800 mb-6 text-center text-base md:text-lg">
                    Protein
                  </h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={chartData} margin={{ top: 30, right: 5, bottom: 40, left: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 11, fill: '#64748b' }}
                        axisLine={false}
                        tickLine={false}
                        dy={10}
                        angle={-45}
                        textAnchor="end"
                      />
                      <YAxis 
                        tick={{ fontSize: 11, fill: '#64748b' }}
                        tickFormatter={(value) => `${formatNumber(value, 0)}%`}
                        axisLine={false}
                        tickLine={false}
                        width={45}
                      />
                      <Tooltip content={<CustomTooltip valueType="percentage" />} cursor={{ fill: '#f8fafc' }} />
                      <Bar 
                        dataKey="protein" 
                        fill="#10b981" 
                        name="Protein" 
                        isAnimationActive={false} 
                        radius={[2, 2, 0, 0]} 
                        barSize={50}
                        label={{ position: 'top', fill: '#1e293b', fontSize: 12, fontWeight: 'bold', formatter: (val) => `${formatNumber(val, 1)}%` }} 
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Carbohydrate Graph */}
                <div className="bg-white p-4 md:p-6 rounded-xl border-2 border-gray-200 shadow-md">
                  <h3 className="font-bold text-gray-800 mb-6 text-center text-base md:text-lg">
                    Carbohydrate
                  </h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={chartData} margin={{ top: 30, right: 5, bottom: 40, left: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 11, fill: '#64748b' }}
                        axisLine={false}
                        tickLine={false}
                        dy={10}
                        angle={-45}
                        textAnchor="end"
                      />
                      <YAxis 
                        tick={{ fontSize: 11, fill: '#64748b' }}
                        tickFormatter={(value) => `${formatNumber(value, 0)}%`}
                        axisLine={false}
                        tickLine={false}
                        width={45}
                      />
                      <Tooltip content={<CustomTooltip valueType="percentage" />} cursor={{ fill: '#f8fafc' }} />
                      <Bar 
                        dataKey="carbs" 
                        fill="#3b82f6" 
                        name="Carbohydrate" 
                        isAnimationActive={false} 
                        radius={[2, 2, 0, 0]} 
                        barSize={50}
                        label={{ position: 'top', fill: '#1e293b', fontSize: 12, fontWeight: 'bold', formatter: (val) => `${formatNumber(val, 1)}%` }} 
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Fat Graph */}
                <div className="bg-white p-4 md:p-6 rounded-xl border-2 border-gray-200 shadow-md">
                  <h3 className="font-bold text-gray-800 mb-6 text-center text-base md:text-lg">
                    Fat
                  </h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={chartData} margin={{ top: 30, right: 5, bottom: 40, left: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 11, fill: '#64748b' }}
                        axisLine={false}
                        tickLine={false}
                        dy={10}
                        angle={-45}
                        textAnchor="end"
                      />
                      <YAxis 
                        tick={{ fontSize: 11, fill: '#64748b' }}
                        tickFormatter={(value) => `${formatNumber(value, 0)}%`}
                        axisLine={false}
                        tickLine={false}
                        width={45}
                      />
                      <Tooltip content={<CustomTooltip valueType="percentage" />} cursor={{ fill: '#f8fafc' }} />
                      <Bar 
                        dataKey="fat" 
                        fill="#a855f7" 
                        name="Fat" 
                        isAnimationActive={false} 
                        radius={[2, 2, 0, 0]} 
                        barSize={50}
                        label={{ position: 'top', fill: '#1e293b', fontSize: 12, fontWeight: 'bold', formatter: (val) => `${formatNumber(val, 1)}%` }} 
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            {!patient && (
              <Button variant="outline" onClick={() => navigate('/patients')} className="w-fit">
                <ArrowLeft className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Back to Patients</span>
                <span className="sm:hidden">Back</span>
              </Button>
            )}
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">{patient ? 'Calculation History' : 'All Calculations'}</h1>
              <p className="text-sm text-muted-foreground">
                {records.length} riwayat tersimpan (Recall Unlimited)
              </p>
            </div>
          </div>
        </div>

        {/* Records List */}
        {loading ? (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto" />
                <p className="text-sm text-muted-foreground mt-2">Loading history...</p>
              </div>
            </CardContent>
          </Card>
        ) : records.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <div className="text-center text-muted-foreground">
                <p>No saved calculations yet</p>
                <p className="text-sm mt-1">Save a calculation from the dashboard to see it here</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {records.map((record) => {
              // Calculate nutritional status for each record
              const recordStatus = calculateNutritionalStatus(
                parseFloat(record.grand_totals.percentage_of_target),
                {
                  protein: record.grand_totals.percentage_protein,
                  carbs: record.grand_totals.percentage_carbs,
                  fat: record.grand_totals.percentage_fat
                }
              );
              
              return (
              <Card key={record.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-xl">{record.patient_name}</CardTitle>
                        <StatusDot 
                          status={recordStatus.status}
                          color={recordStatus.color}
                          reasons={recordStatus.reasons}
                          size="md"
                        />
                      </div>
                      <CardDescription>
                        {new Date(record.date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </CardDescription>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium flex-shrink-0 ${getStatusColor(record.grand_totals.percentage_of_target)}`}>
                      {record.grand_totals.percentage_of_target}% of target
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                    <div>
                      <div className="text-xs text-muted-foreground">Target</div>
                      <div className="text-lg font-semibold">{record.target_energy_kcal} Kcal</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Total Energy</div>
                      <div className="text-lg font-semibold text-primary">
                        {formatNumber(record.grand_totals.energy_kcal)} Kcal
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        {record.grand_totals.percentage_of_target}%
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Protein</div>
                      <div className="text-lg font-semibold">{formatNumber(record.grand_totals.protein_g)}g</div>
                      <div className="text-sm text-muted-foreground mt-1">
                        {record.grand_totals.percentage_protein || '0,0'}%
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Carbs</div>
                      <div className="text-lg font-semibold">{formatNumber(record.grand_totals.carbohydrate_g)}g</div>
                      <div className="text-sm text-muted-foreground mt-1">
                        {record.grand_totals.percentage_carbs || '0,0'}%
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Fat</div>
                      <div className="text-lg font-semibold">{formatNumber(record.grand_totals.fat_g)}g</div>
                      <div className="text-sm text-muted-foreground mt-1">
                        {record.grand_totals.percentage_fat || '0,0'}%
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 pt-3 border-t">
                    <Button size="sm" variant="outline" onClick={() => handleViewDetails(record)} className="w-full sm:w-auto justify-start">
                      <Eye className="h-4 w-4 mr-2" />
                      View Details
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => handleExportExcel(record)}
                      className="w-full sm:w-auto justify-start"
                    >
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      Export Excel
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => handleExportPDF(record)}
                      disabled={exporting === record.id}
                      className="w-full sm:w-auto justify-start"
                    >
                      <FileDown className="h-4 w-4 mr-2" />
                      {exporting === record.id ? 'Exporting...' : 'Export PDF'}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(record.id, record.patient_name)}
                      className="w-full sm:w-auto justify-start"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail Dialog */}
      {selectedRecord && (
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedRecord.patient_name} - Detailed View</DialogTitle>
              <DialogDescription>
                {new Date(selectedRecord.date).toLocaleDateString()}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              {/* Oral/Enteral Items */}
              <div>
                <h3 className="font-semibold text-lg mb-2 text-primary">Oral/Enteral Items</h3>
                {selectedRecord.oral_enteral_items.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No items</p>
                ) : (
                  <div className="space-y-2">
                    {selectedRecord.oral_enteral_items.map((item) => (
                      <div key={item.food_id || item.name} className="p-3 bg-primary/5 rounded-md">
                        <div className="font-medium">{item.name}</div>
                        <div className="text-sm text-muted-foreground">
                          Qty: {item.quantity} × {item.unit} | 
                          {' '}{item.energy_kcal.toFixed(1)} Kcal | 
                          P: {item.protein_g.toFixed(1)}g | 
                          C: {item.carbohydrate_g.toFixed(1)}g | 
                          F: {item.fat_g.toFixed(1)}g
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-3 p-3 bg-primary/10 rounded-md">
                  <div className="font-semibold">Subtotal: {selectedRecord.oral_enteral_totals.energy_kcal.toFixed(1)} Kcal ({selectedRecord.oral_enteral_totals.percentage_of_target}%)</div>
                  <div className="text-sm">
                    P: {selectedRecord.oral_enteral_totals.protein_g.toFixed(1)}g | 
                    C: {selectedRecord.oral_enteral_totals.carbohydrate_g.toFixed(1)}g | 
                    F: {selectedRecord.oral_enteral_totals.fat_g.toFixed(1)}g
                  </div>
                </div>
              </div>

              {/* Parenteral Items */}
              <div>
                <h3 className="font-semibold text-lg mb-2 text-accent-foreground">Parenteral Items</h3>
                {selectedRecord.parenteral_items.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No items</p>
                ) : (
                  <div className="space-y-2">
                    {selectedRecord.parenteral_items.map((item) => (
                      <div key={item.product_id || item.name} className="p-3 bg-accent/10 rounded-md">
                        <div className="font-medium">{item.name}</div>
                        <div className="text-sm text-muted-foreground">
                          Qty: {item.quantity} × {item.unit} | 
                          {' '}{item.energy_kcal.toFixed(1)} Kcal | 
                          P: {item.protein_g.toFixed(1)}g | 
                          C: {item.carbohydrate_g.toFixed(1)}g | 
                          F: {item.fat_g.toFixed(1)}g
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-3 p-3 bg-accent/20 rounded-md">
                  <div className="font-semibold">Subtotal: {selectedRecord.parenteral_totals.energy_kcal.toFixed(1)} Kcal ({selectedRecord.parenteral_totals.percentage_of_target}%)</div>
                  <div className="text-sm">
                    P: {selectedRecord.parenteral_totals.protein_g.toFixed(1)}g | 
                    C: {selectedRecord.parenteral_totals.carbohydrate_g.toFixed(1)}g | 
                    F: {selectedRecord.parenteral_totals.fat_g.toFixed(1)}g
                  </div>
                </div>
              </div>

              {/* Grand Total */}
              <div className="p-4 bg-gradient-to-r from-primary/20 to-accent/20 rounded-lg">
                <h3 className="font-bold text-xl mb-2">Grand Total</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center mb-4">
                  <div>
                    <div className="text-2xl font-bold">{selectedRecord.grand_totals.energy_kcal.toFixed(1)}</div>
                    <div className="text-xs text-muted-foreground">Energy (Kcal)</div>
                    <div className="text-xs font-medium mt-1">{selectedRecord.grand_totals.percentage_of_target}% of target</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{selectedRecord.grand_totals.protein_g.toFixed(1)}</div>
                    <div className="text-xs text-muted-foreground">Protein (g)</div>
                    <div className="text-xs font-medium mt-1">{selectedRecord.grand_totals.percentage_protein}%</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{selectedRecord.grand_totals.carbohydrate_g.toFixed(1)}</div>
                    <div className="text-xs text-muted-foreground">Carbs (g)</div>
                    <div className="text-xs font-medium mt-1">{selectedRecord.grand_totals.percentage_carbs}%</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{selectedRecord.grand_totals.fat_g.toFixed(1)}</div>
                    <div className="text-xs text-muted-foreground">Fat (g)</div>
                    <div className="text-xs font-medium mt-1">{selectedRecord.grand_totals.percentage_fat}%</div>
                  </div>
                </div>
                
                {/* Sodium and Potassium */}
                <div className="grid grid-cols-2 gap-4 text-center pt-3 border-t border-white/20">
                  <div>
                    <div className="text-xl font-bold">
                      {(() => {
                        let sodiumMeq = 0;
                        selectedRecord.oral_enteral_items?.forEach(item => {
                          if (item.sodium_mg) sodiumMeq += item.sodium_mg / 23;
                        });
                        selectedRecord.parenteral_items?.forEach(item => {
                          if (item.sodium_mmol) sodiumMeq += item.sodium_mmol;
                        });
                        return sodiumMeq.toFixed(1);
                      })()}
                    </div>
                    <div className="text-xs text-muted-foreground">Sodium (Na) mEq</div>
                  </div>
                  <div>
                    <div className="text-xl font-bold">
                      {(() => {
                        let potassiumMeq = 0;
                        selectedRecord.oral_enteral_items?.forEach(item => {
                          if (item.potassium_mg) potassiumMeq += item.potassium_mg / 39.1;
                        });
                        selectedRecord.parenteral_items?.forEach(item => {
                          if (item.potassium_mmol) potassiumMeq += item.potassium_mmol;
                        });
                        return potassiumMeq.toFixed(1);
                      })()}
                    </div>
                    <div className="text-xs text-muted-foreground">Potassium (K) mEq</div>
                  </div>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={closeConfirm}
        onConfirm={confirmDialog.onConfirm}
        type={confirmDialog.type}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
      />

      {/* Alert Dialog */}
      <AlertDialog
        open={alertDialog.open}
        onClose={closeAlert}
        type={alertDialog.type}
        title={alertDialog.title}
        message={alertDialog.message}
        buttonText={alertDialog.buttonText}
      />
      </div>
      
      <Footer />
      {/* Back to Top Button */}
      <BackToTop />
    </div>
  );
}
